import styled, { css } from 'styled-components';

import { LabelCSS } from 'components/core/typography/Label';
import Text from 'components/core/typography/Text';

import { BODY_TEXT, BODY_TEXT_TERTIARY } from 'styles/color';
import { DISABLED_INPUT_OPACITY } from 'styles/forms';
import {
  BLUE_500,
  BORDER_RADIUS_200,
  FONT_SIZE_14,
  FONT_WEIGHT_BOLDEST,
  FONT_WEIGHT_REGULAR,
  HEIGHT_800,
  LETTER_SPACING_DENSE,
  NEUTRAL_0,
  NEUTRAL_050,
  NEUTRAL_300,
  RED_050,
  RED_500,
  SPACE_100,
  SPACE_200,
  SPACE_300,
  SPACE_400,
  SPACE_500,
} from 'styles/tokens';
import { LINE_HEIGHT_CONDENSED_ULTRA } from 'styles/typography';

import type { Props } from './InputText';

type InputFieldRowProps = {
  /**
   * Highlights the field row as active (focused/selected)
   */
  $active?: boolean;
  /**
   * Highlights the field row as invalid
   */
  $invalid?: boolean;
  /**
   * When 'none', renders the row unthemed (no background/border) while keeping the affix layout
   */
  $variant?: Props['variant'];
};

interface InputProps extends Omit<Props, 'active' | 'invalid' | 'isLabel' | 'variant'> {
  $active?: Props['active'];
  $invalid?: Props['invalid'];
  $isLabel?: Props['isLabel'];
  $variant?: Props['variant'];
}

export const InputContainer = styled.div`
  position: relative;
  width: 100%;
`;

export const InputPrefix = styled(Text)<{ $isInteractive?: boolean }>`
  cursor: ${props => (props.$isInteractive ? 'pointer' : 'auto')};
  font-weight: ${FONT_WEIGHT_BOLDEST};
  pointer-events: ${props => (props.$isInteractive ? 'auto' : 'none')};
  user-select: none;
`;

export const InputSuffix = styled(Text)<{ $color?: string; $isInteractive?: boolean }>`
  /* Flex-center the child so icon (SVG) affixes align to the row without the
     baseline/line-height gap that inline text flow would add to their height. */
  align-items: center;
  cursor: ${props => (props.$isInteractive ? 'pointer' : 'auto')};
  display: inline-flex;
  font-weight: ${FONT_WEIGHT_BOLDEST};
  pointer-events: ${props => (props.$isInteractive ? 'auto' : 'none')};
  user-select: none;
  ${props =>
    props.$color &&
    css`
      color: ${props.$color};
    `}
`;

const InputCSS = css`
  background: transparent;
  border: none;
  color: ${BODY_TEXT};
  font-size: ${FONT_SIZE_14};
  font-weight: ${FONT_WEIGHT_REGULAR};
  letter-spacing: ${LETTER_SPACING_DENSE};
  line-height: ${LINE_HEIGHT_CONDENSED_ULTRA};
  outline: none;
  position: relative;
  width: 100%;

  &:disabled {
    opacity: ${DISABLED_INPUT_OPACITY};
  }

  /* stylelint-disable -- Hide arrows/spinners from number input */
  /* Chrome, Safari, Edge, Opera */
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  /* Firefox */
  &[type='number'] {
    -moz-appearance: textfield;
  }
  /* stylelint-enable */
  &::placeholder {
    color: ${BODY_TEXT_TERTIARY};
  }

  &:placeholder-shown {
    text-overflow: ellipsis;
  }
`;

export const Input = styled.input<InputProps>`
  ${InputCSS}
  ${props => props.$variant !== 'none' && baseTheme} /** Apply base theme if the 'theme' prop is not set to 'none' */
  ${props => props.$invalid && invalidInput} /** Highlight red if invalid */
  ${props => props.$active && activeInput} /** If active highlight text, InputPrefix */
  
  &::-ms-clear {
    /* Hide IE11 putting by default a close icon to clear inputs, and a reveal eye icon to show password inputs */
    display: none;
  }

  &::-ms-reveal {
    display: none;
  }
  ${props =>
    props.$isLabel &&
    css`
      margin: ${SPACE_300} 0 ${SPACE_500};
    `};
`;

export const TextArea = styled.textarea<{
  $active?: Props['active'];
  $invalid?: Props['invalid'];
  $label?: Props['label'];
  $variant?: Props['variant'];
}>`
  ${InputCSS}
  ${props => props.$variant !== 'none' && baseTheme} /** Apply base theme if the 'theme' prop is not set to 'none' */
  ${props => props.$invalid && invalidInput} /** Highlight red if invalid */
  ${props => props.$active && activeInput} /** If active highlight text, InputPrefix */
  color: ${BODY_TEXT};
  height: 100%;
  padding: ${SPACE_300};
  resize: none;
  ${props =>
    props.$label
      ? css`
          margin: ${SPACE_300} 0 ${SPACE_500};
        `
      : ''};
`;

/*
 * Themed field-box decoration: grey fill, border, rounded corners and sizing. Split
 * out from baseTheme so the field row can opt out of it for the 'none' variant.
 * Note: the box owns NO outer margin — the gap below a field's label is owned by the
 * layout that places the field (e.g. the builder's StepFieldContainerInner). Keeping
 * margin here used to leak an 8px offset into every flex-row consumer (range, toggle,
 * dialogs) that had to neutralise it, so it lives with the label-owning wrapper instead.
 */
const fieldBoxTheme = css`
  background: ${NEUTRAL_050};
  border: 1px solid ${NEUTRAL_300};
  border-radius: ${BORDER_RADIUS_200};
  min-height: ${HEIGHT_800};
  padding: ${SPACE_200} ${SPACE_400};
`;

export const baseTheme = css`
  align-items: center;
  display: flex;
  width: 100%;
  ${fieldBoxTheme}
`;

const activeInput = css`
  background: ${NEUTRAL_0};
  border: 1px solid ${BLUE_500};
  color: ${BLUE_500};
`;

const invalidInput = css`
  background: ${RED_050};
  border: 1px solid ${RED_500};
  color: ${RED_500};
`;

const fieldRowActive = css`
  background: ${NEUTRAL_0};
  border: 1px solid ${BLUE_500};

  & > input,
  & > ${InputPrefix}, & > ${InputSuffix} {
    color: ${BLUE_500};
  }
`;

const fieldRowInvalid = css`
  background: ${RED_050};
  border: 1px solid ${RED_500};

  & > input,
  & > ${InputPrefix}, & > ${InputSuffix} {
    color: ${RED_500};
  }
`;

export const InputFieldRow = styled.div<InputFieldRowProps>`
  align-items: center;
  display: flex;
  gap: ${SPACE_100};
  width: 100%;

  /* Suppress the themed box for the 'none' variant so the row renders unthemed
     (transparent, borderless) while still laying out its affixes via flexbox. */
  ${props => props.$variant !== 'none' && fieldBoxTheme}

  & > input {
    flex: 1 1 0;
    min-width: 0;
    width: auto;
  }

  /* Dim the entire field box — border, fill, input, and affixes — as one unit when
     the input is disabled, matching the single-opacity disabled treatment InputCSS
     gives a plain themed input (whose box lives on the input itself). The children
     are reset to full opacity so only the row's opacity dims them; otherwise the
     input's own :disabled opacity and the affix opacity would stack into a darker
     dim than the box. */
  &:has(:disabled) {
    opacity: ${DISABLED_INPUT_OPACITY};
  }

  &:has(:disabled) > input,
  &:has(:disabled) > ${InputPrefix}, &:has(:disabled) > ${InputSuffix} {
    opacity: 1;
  }

  ${props => props.$invalid && fieldRowInvalid}
  ${props => props.$active && fieldRowActive}
`;

export const LabelStyle = styled.label<Props>`
  ${LabelCSS}
  ${props =>
    props.invalid &&
    css`
      color: ${RED_500};
    `}
`;
